// @ts-nocheck
/**
 * Institutional Analytics Component
 * Provides comprehensive analytics dashboard for institutional metrics
 * Includes real-time data visualization and reporting capabilities
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Heading,
  Tag,
  Button,
  Dropdown,
  DatePicker,
  DatePickerInput,
  Toggle,
  InlineNotification,
  Loading,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import {
  Analytics,
  ChartLine,
  Growth,
  ArrowDown,
  User,
  Time,
  CheckmarkFilled,
  Warning,
  Dashboard,
  Download,
  Renew,
  Settings,
  View,
} from '@carbon/icons-react';
import { LineChart, SimpleBarChart as BarChart, DonutChart, AreaChart } from '@carbon/charts-react';

interface AnalyticsData {
  engagement: {
    daily_active_users: number;
    weekly_active_users: number;
    monthly_active_users: number;
    average_session_duration: number;
    bounce_rate: number;
    retention_rate: number;
  };
  performance: {
    total_assessments: number;
    average_score: number;
    completion_rate: number;
    improvement_rate: number;
  };
  usage: {
    content_views: number;
    feature_usage: Record<string, number>;
    device_breakdown: Record<string, number>;
  };
  trends: {
    user_growth: Array<{ date: string; users: number }>;
    engagement_trends: Array<{ date: string; engagement: number }>;
    performance_trends: Array<{ date: string; score: number }>;
  };
}

interface ComplianceMetrics {
  ferpa_compliance: {
    status: 'compliant' | 'warning' | 'critical';
    last_audit: string;
    issues: number;
  };
  coppa_compliance: {
    status: 'compliant' | 'warning' | 'critical';
    last_audit: string;
    issues: number;
  };
  data_retention: {
    policies_active: number;
    automated_deletion: boolean;
    last_cleanup: string;
  };
}

const InstitutionalAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [complianceMetrics, setComplianceMetrics] = useState<ComplianceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [realTimeEnabled, setRealTimeEnabled] = useState(false);

  useEffect(() => {
    loadAnalyticsData();
  }, [selectedTimeRange, selectedDistrict]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (realTimeEnabled) {
      interval = setInterval(loadAnalyticsData, 30000); // Update every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [realTimeEnabled, selectedTimeRange, selectedDistrict]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Mock data - in real implementation, fetch from API
      const mockAnalytics: AnalyticsData = {
        engagement: {
          daily_active_users: 2847,
          weekly_active_users: 8932,
          monthly_active_users: 24567,
          average_session_duration: 28.5,
          bounce_rate: 23.4,
          retention_rate: 78.9,
        },
        performance: {
          total_assessments: 15678,
          average_score: 82.4,
          completion_rate: 89.7,
          improvement_rate: 12.3,
        },
        usage: {
          content_views: 145782,
          feature_usage: {
            'Interactive Lessons': 45,
            'Assessment Tools': 32,
            'Progress Tracking': 28,
            'Discussion Forums': 18,
            'Video Content': 35,
            'Practice Exercises': 42,
          },
          device_breakdown: {
            Desktop: 52,
            Tablet: 31,
            Mobile: 17,
          },
        },
        trends: {
          user_growth: [
            { date: '2024-11-01', users: 20000 },
            { date: '2024-11-08', users: 21200 },
            { date: '2024-11-15', users: 22800 },
            { date: '2024-11-22', users: 23900 },
            { date: '2024-11-29', users: 24567 },
            { date: '2024-12-06', users: 25200 },
            { date: '2024-12-13', users: 25800 },
          ],
          engagement_trends: [
            { date: '2024-11-01', engagement: 72 },
            { date: '2024-11-08', engagement: 74 },
            { date: '2024-11-15', engagement: 76 },
            { date: '2024-11-22', engagement: 79 },
            { date: '2024-11-29', engagement: 78 },
            { date: '2024-12-06', engagement: 80 },
            { date: '2024-12-13', engagement: 82 },
          ],
          performance_trends: [
            { date: '2024-11-01', score: 78.5 },
            { date: '2024-11-08', score: 79.2 },
            { date: '2024-11-15', score: 80.1 },
            { date: '2024-11-22', score: 81.3 },
            { date: '2024-11-29', score: 81.8 },
            { date: '2024-12-06', score: 82.0 },
            { date: '2024-12-13', score: 82.4 },
          ],
        },
      };

      const mockCompliance: ComplianceMetrics = {
        ferpa_compliance: {
          status: 'compliant',
          last_audit: '2024-11-15',
          issues: 0,
        },
        coppa_compliance: {
          status: 'warning',
          last_audit: '2024-10-28',
          issues: 2,
        },
        data_retention: {
          policies_active: 5,
          automated_deletion: true,
          last_cleanup: '2024-12-01',
        },
      };

      setAnalyticsData(mockAnalytics);
      setComplianceMetrics(mockCompliance);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'critical':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getComplianceIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckmarkFilled style={{ color: '#24a148' }} />;
      case 'warning':
        return <Warning style={{ color: '#f1c21b' }} />;
      case 'critical':
        return <Warning style={{ color: '#fa4d56' }} />;
      default:
        return null;
    }
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) {
      return <Growth style={{ color: '#24a148' }} />;
    } else if (current < previous) {
      return <ArrowDown style={{ color: '#fa4d56' }} />;
    }
    return null;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Chart configurations
  const userGrowthChartOptions = {
    title: 'User Growth Trend',
    axes: {
      bottom: {
        title: 'Date',
        mapsTo: 'date',
        scaleType: 'time',
      },
      left: {
        title: 'Total Users',
        mapsTo: 'users',
      },
    },
    height: '300px',
    color: {
      scale: {
        users: '#0f62fe',
      },
    },
  };

  const engagementChartOptions = {
    title: 'Engagement Trends',
    axes: {
      bottom: {
        title: 'Date',
        mapsTo: 'date',
        scaleType: 'time',
      },
      left: {
        title: 'Engagement Score',
        mapsTo: 'engagement',
      },
    },
    height: '300px',
    color: {
      scale: {
        engagement: '#0f62fe',
      },
    },
  };

  const featureUsageChartOptions = {
    title: 'Feature Usage Distribution',
    height: '300px',
    donut: {
      center: {
        label: 'Features',
      },
    },
  };

  const deviceBreakdownChartOptions = {
    title: 'Device Usage Breakdown',
    height: '300px',
    donut: {
      center: {
        label: 'Devices',
      },
    },
  };

  // Transform data for charts
  const userGrowthData =
    analyticsData?.trends.user_growth.map(point => ({
      group: 'users',
      date: point.date,
      users: point.users,
    })) || [];

  const engagementData =
    analyticsData?.trends.engagement_trends.map(point => ({
      group: 'engagement',
      date: point.date,
      engagement: point.engagement,
    })) || [];

  const featureUsageData = Object.entries(analyticsData?.usage.feature_usage || {}).map(([feature, usage]) => ({
    group: feature,
    value: usage,
  }));

  const deviceBreakdownData = Object.entries(analyticsData?.usage.device_breakdown || {}).map(([device, usage]) => ({
    group: device,
    value: usage,
  }));

  if (loading && !analyticsData) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Loading description="Loading institutional analytics..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <Heading size="lg">
            <Analytics style={{ marginRight: '0.5rem' }} />
            Institutional Analytics
          </Heading>
          <div>Comprehensive insights into platform usage and performance</div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Dropdown
            id="time-range-select"
            label="Time Range"
            items={[
              { id: '7d', text: 'Last 7 days' },
              { id: '30d', text: 'Last 30 days' },
              { id: '90d', text: 'Last 90 days' },
              { id: '1y', text: 'Last year' },
            ]}
            selectedItem={{ id: selectedTimeRange, text: selectedTimeRange }}
            onChange={({ selectedItem }) => setSelectedTimeRange(selectedItem?.id || '30d')}
            size="sm"
          />

          <Dropdown
            id="district-select"
            label="District"
            items={[
              { id: 'all', text: 'All Districts' },
              { id: 'dist_001', text: 'Springfield USD' },
              { id: 'dist_002', text: 'Riverside SD' },
            ]}
            selectedItem={{ id: selectedDistrict, text: selectedDistrict }}
            onChange={({ selectedItem }) => setSelectedDistrict(selectedItem?.id || 'all')}
            size="sm"
          />

          <Toggle
            id="realtime-toggle"
            labelText="Real-time"
            size="sm"
            toggled={realTimeEnabled}
            onToggle={setRealTimeEnabled}
          />

          <Button kind="ghost" size="sm" renderIcon={Renew} onClick={loadAnalyticsData}>
            Refresh
          </Button>

          <Button kind="tertiary" size="sm" renderIcon={Download}>
            Export Report
          </Button>
        </div>
      </div>

      {realTimeEnabled && (
        <InlineNotification
          kind="info"
          title="Real-time Mode Enabled"
          subtitle="Data updates every 30 seconds"
          lowContrast
          hideCloseButton
          style={{ marginBottom: '1rem' }}
        />
      )}

      <Tabs>
        <TabList>
          <Tab onClick={() => setActiveTab('overview')}>Overview</Tab>
          <Tab onClick={() => setActiveTab('engagement')}>Engagement</Tab>
          <Tab onClick={() => setActiveTab('performance')}>Performance</Tab>
          <Tab onClick={() => setActiveTab('compliance')}>Compliance</Tab>
          <Tab onClick={() => setActiveTab('trends')}>Trends</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            {activeTab === 'overview' && analyticsData && (
              <>
                {/* Key Metrics */}
                <Grid style={{ marginBottom: '2rem' }}>
                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <User size={24} style={{ color: '#0f62fe' }} />
                          <span>Active Users</span>
                        </div>
                        <Heading size="xl">{formatNumber(analyticsData.engagement.daily_active_users)}</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          Daily active users (+12.3% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>

                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ChartLine size={24} style={{ color: '#0f62fe' }} />
                          <span>Engagement Score</span>
                        </div>
                        <Heading size="xl">{analyticsData.engagement.retention_rate}%</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          User retention rate (+2.1% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>

                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <CheckmarkFilled size={24} style={{ color: '#24a148' }} />
                          <span>Performance</span>
                        </div>
                        <Heading size="xl">{analyticsData.performance.average_score}%</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          Average assessment score (+1.8% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>

                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Time size={24} style={{ color: '#f1c21b' }} />
                          <span>Session Duration</span>
                        </div>
                        <Heading size="xl">{analyticsData.engagement.average_session_duration}m</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          Average session length (+5.2% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>

                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <View size={24} style={{ color: '#8a3ffc' }} />
                          <span>Content Views</span>
                        </div>
                        <Heading size="xl">{formatNumber(analyticsData.usage.content_views)}</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          Total content interactions (+8.7% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>

                  <Column lg={4}>
                    <Tile style={{ padding: '1.5rem' }}>
                      <Stack gap={3}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Dashboard size={24} style={{ color: '#fa4d56' }} />
                          <span>Completion Rate</span>
                        </div>
                        <Heading size="xl">{analyticsData.performance.completion_rate}%</Heading>
                        <span size="sm" style={{ color: '#6f6f6f' }}>
                          Assessment completion rate (+3.4% vs last period)
                        </span>
                      </Stack>
                    </Tile>
                  </Column>
                </Grid>

                {/* Charts Row */}
                <Grid>
                  <Column lg={8}>
                    <Tile style={{ padding: '1rem' }}>
                      <LineChart data={userGrowthData} options={userGrowthChartOptions} />
                    </Tile>
                  </Column>

                  <Column lg={8}>
                    <Tile style={{ padding: '1rem' }}>
                      <DonutChart data={featureUsageData} options={featureUsageChartOptions} />
                    </Tile>
                  </Column>
                </Grid>
              </>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'engagement' && analyticsData && (
              <Grid>
                <Column lg={16}>
                  <Tile style={{ padding: '1rem', marginBottom: '1rem' }}>
                    <AreaChart data={engagementData} options={engagementChartOptions} />
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Engagement Metrics
                    </Heading>
                    <Stack gap={4}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Daily Active Users</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatNumber(analyticsData.engagement.daily_active_users)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Weekly Active Users</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatNumber(analyticsData.engagement.weekly_active_users)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Monthly Active Users</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatNumber(analyticsData.engagement.monthly_active_users)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Bounce Rate</span>
                        <span style={{ fontWeight: 'bold' }}>{analyticsData.engagement.bounce_rate}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Retention Rate</span>
                        <span style={{ fontWeight: 'bold', color: '#24a148' }}>
                          {analyticsData.engagement.retention_rate}%
                        </span>
                      </div>
                    </Stack>
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1rem' }}>
                    <DonutChart data={deviceBreakdownData} options={deviceBreakdownChartOptions} />
                  </Tile>
                </Column>
              </Grid>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'performance' && analyticsData && (
              <Grid>
                <Column lg={16}>
                  <Tile style={{ padding: '1rem', marginBottom: '1rem' }}>
                    <LineChart
                      data={analyticsData.trends.performance_trends.map(point => ({
                        group: 'score',
                        date: point.date,
                        score: point.score,
                      }))}
                      options={{
                        title: 'Performance Trends',
                        axes: {
                          bottom: {
                            title: 'Date',
                            mapsTo: 'date',
                            scaleType: 'time',
                          },
                          left: {
                            title: 'Average Score',
                            mapsTo: 'score',
                          },
                        },
                        height: '300px',
                        color: {
                          scale: {
                            score: '#24a148',
                          },
                        },
                      }}
                    />
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Performance Summary
                    </Heading>
                    <Stack gap={4}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Assessments</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatNumber(analyticsData.performance.total_assessments)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Average Score</span>
                        <span style={{ fontWeight: 'bold', color: '#24a148' }}>
                          {analyticsData.performance.average_score}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Completion Rate</span>
                        <span style={{ fontWeight: 'bold' }}>{analyticsData.performance.completion_rate}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Improvement Rate</span>
                        <span style={{ fontWeight: 'bold', color: '#24a148' }}>
                          +{analyticsData.performance.improvement_rate}%
                        </span>
                      </div>
                    </Stack>
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Grade Distribution
                    </Heading>
                    <BarChart
                      data={[
                        { group: 'A (90-100%)', value: 35 },
                        { group: 'B (80-89%)', value: 28 },
                        { group: 'C (70-79%)', value: 22 },
                        { group: 'D (60-69%)', value: 10 },
                        { group: 'F (0-59%)', value: 5 },
                      ]}
                      options={{
                        title: 'Grade Distribution',
                        axes: {
                          bottom: {
                            title: 'Grade Range',
                            mapsTo: 'group',
                          },
                          left: {
                            title: 'Percentage of Students',
                            mapsTo: 'value',
                          },
                        },
                        height: '250px',
                      }}
                    />
                  </Tile>
                </Column>
              </Grid>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'compliance' && complianceMetrics && (
              <Grid>
                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      FERPA Compliance
                    </Heading>
                    <Stack gap={3}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getComplianceIcon(complianceMetrics.ferpa_compliance.status)}
                        <Tag type={getComplianceColor(complianceMetrics.ferpa_compliance.status)} size="sm">
                          {complianceMetrics.ferpa_compliance.status.toUpperCase()}
                        </Tag>
                      </div>
                      <span size="sm">
                        Last Audit: {new Date(complianceMetrics.ferpa_compliance.last_audit).toLocaleDateString()}
                      </span>
                      <span size="sm">Issues Found: {complianceMetrics.ferpa_compliance.issues}</span>
                    </Stack>
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      COPPA Compliance
                    </Heading>
                    <Stack gap={3}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {getComplianceIcon(complianceMetrics.coppa_compliance.status)}
                        <Tag type={getComplianceColor(complianceMetrics.coppa_compliance.status)} size="sm">
                          {complianceMetrics.coppa_compliance.status.toUpperCase()}
                        </Tag>
                      </div>
                      <span size="sm">
                        Last Audit: {new Date(complianceMetrics.coppa_compliance.last_audit).toLocaleDateString()}
                      </span>
                      <span size="sm">Issues Found: {complianceMetrics.coppa_compliance.issues}</span>
                      {complianceMetrics.coppa_compliance.issues > 0 && (
                        <InlineNotification
                          kind="warning"
                          title="Compliance Issues"
                          subtitle="Review and address COPPA compliance issues"
                          lowContrast
                          hideCloseButton
                        />
                      )}
                    </Stack>
                  </Tile>
                </Column>

                <Column lg={16}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Data Retention Management
                    </Heading>
                    <Grid>
                      <Column lg={5}>
                        <Stack gap={3}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Active Policies</span>
                            <span style={{ fontWeight: 'bold' }}>
                              {complianceMetrics.data_retention.policies_active}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Automated Deletion</span>
                            <Tag type={complianceMetrics.data_retention.automated_deletion ? 'green' : 'red'} size="sm">
                              {complianceMetrics.data_retention.automated_deletion ? 'Enabled' : 'Disabled'}
                            </Tag>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Last Cleanup</span>
                            <span size="sm">
                              {new Date(complianceMetrics.data_retention.last_cleanup).toLocaleDateString()}
                            </span>
                          </div>
                        </Stack>
                      </Column>
                      <Column lg={11}>
                        <Stack gap={2}>
                          <Button kind="tertiary" size="sm">
                            Configure Retention Policies
                          </Button>
                          <Button kind="ghost" size="sm">
                            Run Manual Cleanup
                          </Button>
                          <Button kind="ghost" size="sm">
                            Export Compliance Report
                          </Button>
                        </Stack>
                      </Column>
                    </Grid>
                  </Tile>
                </Column>
              </Grid>
            )}
          </TabPanel>

          <TabPanel>
            {activeTab === 'trends' && analyticsData && (
              <Grid>
                <Column lg={16}>
                  <Tile style={{ padding: '1rem', marginBottom: '1rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Comparative Trends
                    </Heading>
                    <LineChart
                      data={[
                        ...analyticsData.trends.user_growth.map(point => ({
                          group: 'User Growth',
                          date: point.date,
                          value: point.users / 1000, // Scale for comparison
                        })),
                        ...analyticsData.trends.engagement_trends.map(point => ({
                          group: 'Engagement Score',
                          date: point.date,
                          value: point.engagement,
                        })),
                        ...analyticsData.trends.performance_trends.map(point => ({
                          group: 'Performance Score',
                          date: point.date,
                          value: point.score,
                        })),
                      ]}
                      options={{
                        title: 'Key Metrics Trends',
                        axes: {
                          bottom: {
                            title: 'Date',
                            mapsTo: 'date',
                            scaleType: 'time',
                          },
                          left: {
                            title: 'Value',
                            mapsTo: 'value',
                          },
                        },
                        height: '400px',
                      }}
                    />
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Trend Analysis
                    </Heading>
                    <Stack gap={4}>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>User Growth</span>
                        <span size="sm" style={{ color: '#24a148' }}>
                          +18.4% growth rate over selected period
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Engagement</span>
                        <span size="sm" style={{ color: '#24a148' }}>
                          +13.9% improvement in engagement score
                        </span>
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Performance</span>
                        <span size="sm" style={{ color: '#24a148' }}>
                          +5.0% increase in average assessment scores
                        </span>
                      </div>
                    </Stack>
                  </Tile>
                </Column>

                <Column lg={8}>
                  <Tile style={{ padding: '1.5rem' }}>
                    <Heading size="md" style={{ marginBottom: '1rem' }}>
                      Forecasting
                    </Heading>
                    <Stack gap={4}>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Next Month Projection</span>
                        <span size="sm">Expected 26,800 monthly active users</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Performance Target</span>
                        <span size="sm">Projected 84.2% average score</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: 'bold' }}>Engagement Goal</span>
                        <span size="sm">Target 85% retention rate</span>
                      </div>
                    </Stack>
                  </Tile>
                </Column>
              </Grid>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default InstitutionalAnalytics;
